/**
 * RulesAnalyticsFilters Component
 *
 * Inline filter controls for rule compliance analytics
 */

import React, { useCallback, useMemo } from 'react';
import { DateRangeSelector } from '@components/DateRangeSelector/DateRangeSelector';
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useGetDashboardProvidersQuery } from '../../store/api';

export interface RulesAnalyticsFilters {
  /** Start date (ISO format) */
  dateFrom?: string;
  /** End date (ISO format) */
  dateTo?: string;
  /** Period in days (alternative to date range) */
  days?: number;
  /** Selected provider IDs */
  userIds?: string[];
  /** Selected rule IDs (optional) */
  ruleIds?: string[];
  /** Comparison mode enabled */
  comparisonMode?: boolean;
}

interface RulesAnalyticsFiltersProps {
  /** Current filter values */
  filters: RulesAnalyticsFilters;
  /** Callback when filters change */
  onFiltersChange: (filters: RulesAnalyticsFilters) => void;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Whether to show comparison mode toggle */
  showComparisonMode?: boolean;
}

export const RulesAnalyticsFilters: React.FC<RulesAnalyticsFiltersProps> = ({
  filters,
  onFiltersChange,
  isLoading = false,
  showComparisonMode = true,
}) => {
  const { jotauditDashboard } = useEventTracking();

  // Fetch providers for multi-select
  const { data: providersData } = useGetDashboardProvidersQuery({});

  const providers = useMemo(() => providersData?.providers || [], [providersData]);

  const handleDateRangeChange = useCallback(
    (range: { start: Date | null; end: Date | null }) => {
      const previousValue = filters.dateFrom
        ? `${filters.dateFrom}_${filters.dateTo}`
        : filters.days?.toString() || 'none';
      const newValue = range.start
        ? `${range.start.toISOString().split('T')[0]}_${range.end?.toISOString().split('T')[0]}`
        : 'none';

      // Track filter change
      jotauditDashboard.rulesFilterChanged({
        filter_type: 'date_range',
        previous_value: previousValue,
        new_value: newValue,
      });

      onFiltersChange({
        ...filters,
        dateFrom: range.start ? range.start.toISOString().split('T')[0] : undefined,
        dateTo: range.end ? range.end.toISOString().split('T')[0] : undefined,
        days: undefined, // Clear days when using date range
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const handleDaysChange = useCallback(
    (days: number | undefined) => {
      const previousValue = filters.days?.toString() || 'none';
      const newValue = days?.toString() || 'none';

      // Track filter change
      jotauditDashboard.rulesFilterChanged({
        filter_type: 'days',
        previous_value: previousValue,
        new_value: newValue,
      });

      onFiltersChange({
        ...filters,
        days,
        dateFrom: undefined, // Clear date range when using days
        dateTo: undefined,
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const handleProvidersChange = useCallback(
    (_event: unknown, value: Array<{ user_id: string; provider_name?: string | null }>) => {
      const previousCount = filters.userIds?.length || 0;
      const newCount = value.length;

      // Track filter change
      jotauditDashboard.rulesFilterChanged({
        filter_type: 'clinicians',
        previous_value: previousCount.toString(),
        new_value: newCount.toString(),
        clinicians_count: newCount,
      });

      onFiltersChange({
        ...filters,
        userIds: value.length > 0 ? value.map(p => p.user_id) : undefined,
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const handleComparisonModeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;

      // Track comparison mode toggle
      jotauditDashboard.comparisonModeToggled({
        enabled,
        clinicians_count: filters.userIds?.length || 0,
      });

      onFiltersChange({
        ...filters,
        comparisonMode: enabled,
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const selectedProviders = useMemo(() => {
    if (!filters.userIds || filters.userIds.length === 0) return [];
    return providers.filter(p => filters.userIds?.includes(p.user_id));
  }, [filters.userIds, providers]);

  const dateRangeValue = useMemo(() => {
    if (filters.dateFrom && filters.dateTo) {
      return {
        start: new Date(filters.dateFrom),
        end: new Date(filters.dateTo),
      };
    }
    return { start: null, end: null };
  }, [filters.dateFrom, filters.dateTo]);

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.dateFrom ||
      filters.dateTo ||
      filters.days ||
      (filters.userIds && filters.userIds.length > 0) ||
      filters.comparisonMode
    );
  }, [filters]);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {/* Date Range Selector */}
        <Box sx={{ minWidth: 250 }}>
          <DateRangeSelector
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            label="Date Range"
            size="small"
            disabled={isLoading}
            fullWidth
          />
        </Box>

        {/* Days Preset Chips */}
        {!filters.dateFrom && !filters.dateTo && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Quick:
            </Typography>
            {[7, 30, 90].map(days => (
              <Chip
                key={days}
                label={`${days}d`}
                onClick={() => handleDaysChange(filters.days === days ? undefined : days)}
                color={filters.days === days ? 'primary' : 'default'}
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        )}

        {/* Provider Multi-Select */}
        <Box sx={{ minWidth: 250, flex: 1 }}>
          <Autocomplete
            multiple
            options={providers}
            getOptionLabel={option => option.provider_name || option.user_id}
            value={selectedProviders}
            onChange={handleProvidersChange}
            disabled={isLoading}
            size="small"
            renderInput={params => (
              <TextField {...params} label="Providers" placeholder="All providers" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.user_id}
                  label={option.provider_name || option.user_id}
                  size="small"
                />
              ))
            }
          />
        </Box>

        {/* Comparison Mode Toggle */}
        {showComparisonMode && (
          <FormControlLabel
            control={
              <Switch
                checked={filters.comparisonMode || false}
                onChange={handleComparisonModeChange}
                size="small"
                disabled={isLoading}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Compare Providers
              </Typography>
            }
          />
        )}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Chip
            label="Clear"
            onClick={() =>
              onFiltersChange({
                dateFrom: undefined,
                dateTo: undefined,
                days: undefined,
                userIds: undefined,
                ruleIds: undefined,
                comparisonMode: false,
              })
            }
            size="small"
            sx={{ cursor: 'pointer', ml: 'auto' }}
          />
        )}
      </Box>
    </Box>
  );
};

